import { formatDate, LowerCasePipe, TitleCasePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { DocumentTagsService } from '@api/accounts/byId/tags/tags.service';
import { ApiService } from '@api/api.service';
import { SearchCriteria, SearchCriteriaOperator, Template } from '@api/types';
import { SnakeCaseToHumanPipe } from '@pipes/snake-case-to-human.pipe';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { documentSearchFields, emptyAccount, emptyScanner, emptyUser } from '@shared/types/empty-type-objects';
import { SearchFormValues } from '@shared/types/search-types';
import { TableDataType } from '@shared/types/table';
import { get, uniqBy } from 'lodash';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

@Component({
  selector: 'rav-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss']
})
export class AdvancedSearchComponent implements OnInit, AfterViewInit {
  @Input() advancedSearchIndex: number;
  @Input() isGlobalSearch = false;
  @Input() globalSearchSubmitted: Observable<void>;
  @Input() dataType: TableDataType;
  @Input() plusButtonEnabled = false;

  @Output() templateChanged = new EventEmitter<void>();
  @Output() searchRemoved = new EventEmitter<number>();
  @Output() newAdvancedSearch = new EventEmitter<SearchCriteria | SearchCriteria[]>();
  @Output() plusButtonClicked = new EventEmitter<void>();

  @ViewChild('searchValue') searchValue: ElementRef;
  hasError: boolean;
  lastSearchField: string;
  searchObject: SearchFormValues;
  templateFields: Array<any> = [];
  searchFields: { [key: string]: string };
  userTemplates: Array<{ [key: string]: string }> = [];
  addressFields = [
    { value: 'City', label: 'City' },
    { value: 'Country', label: 'Country' },
    { value: 'State', label: 'State' },
    { value: 'Street_1', label: 'Street 1' },
    { value: 'Street_2', label: 'Street 2' },
    { value: 'Zipcode', label: 'Zipcode' }
  ];

  tags$ = this.tagService.getAllTags(this.storageService.userWithToken.account_id);

  // for table search
  private templatesLoadedPromise: Promise<void>;
  private optionsByField: { [key: string]: { [key: string]: string } } = {};
  private optionsForTemplateFields: { [key: string]: { [key: string]: string } } = {};
  private fieldsList = [
    'title',
    'document_tag_ids',
    'source',
    'version',
    'shared_at',
    'created_at',
    'updated_at',
    'description',
    'template_id',
    'control_number',
    'template_data'
  ];
  // All the criteria options that can be shown on the advanced search
  private searchOptions = {
    partial: 'Contains',
    '=': 'Equals(=)',
    '>': 'Greater Than(>)',
    '<': 'Less Than(<)',
    '!=': 'Different than(!=)',
    '>=': 'Greater or Equal Than(>=)',
    '<=': 'Less or Equal Than(<=)',
    in: 'In(separate words by commas)',
    '!in': 'Not in (separate words by commas)',
    range: 'Date Range'
  };
  // All the field types that the user can select in a template
  private templateFieldTypes = [
    'text',
    'email',
    'time',
    'phone',
    'currency',
    'radio',
    'number',
    'date',
    'URL',
    'address',
    'dropdown',
    'checkbox',
    'range'
  ];

  constructor(
    private api: ApiService,
    private changeDetector: ChangeDetectorRef,
    private lowercasePipe: LowerCasePipe,
    private snakeCasePipe: SnakeCaseToHumanPipe,
    private titlePipe: TitleCasePipe,
    private tagService: DocumentTagsService,
    private storageService: LocalStorageService
  ) {
    this.initializeSearch();

    // Creates the filtered options for search criteria dropdown
    this.fieldsList.forEach((field) => {
      this.optionsByField[field] = this.getOptionsByField(field);
    });

    // Creates the filtered options for search criteria dropdown for template type fields
    this.templateFieldTypes.forEach((type) => {
      this.optionsForTemplateFields[type] = this.getOptionsByFieldType(type);
    });
  }

  async ngOnInit() {
    if (this.isGlobalSearch) {
      this.templatesLoadedPromise = this.loadUserTemplates();

      // It must wait for templates to remove unnecessary fields on advance search
      await this.templatesLoadedPromise;
    }
    switch (this.dataType) {
      case TableDataType.User:
        this.searchFields = this.getSearchFields(emptyUser);
        break;
      case TableDataType.Account:
        this.searchFields = this.getSearchFields(emptyAccount);
        break;
      case TableDataType.Scanner:
        this.searchFields = this.getSearchFields(emptyScanner);
        break;
      default:
      case TableDataType.Document:
        // In order to not delete the key in the original object needs to be a copy
        this.searchFields = { ...documentSearchFields };

        if (this.templateFields.length === 0) delete this.searchFields.template_data;
        break;
    }
  }

  ngAfterViewInit() {
    if (!this.isGlobalSearch) {
      fromEvent(this.searchValue.nativeElement, 'keyup')
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => {
            if (!this.searchObject.value) return;
            this.sendTableSearch(this.searchObject.field, this.searchObject.value);
          })
        )
        .subscribe();
    } else {
      this.globalSearchSubmitted.subscribe(() => {
        this.sendGlobalSearch();
      });
    }
  }

  /**
   * Triggered when a new search field is selected for the table-search
   *
   * @param event MatSelectChange object
   */
  changeSearchField(event: MatSelectChange) {
    // Resets the template field type since changes on field selection
    this.searchObject.templateField = '';
    this.searchObject.templateFieldType = '';

    if (this.isGlobalSearch) return;

    // Sends the search values for table search in the admin page
    this.sendTableSearch(this.lastSearchField, '');
    this.sendTableSearch(this.searchObject.field, this.searchObject.value);
  }

  /**
   * Saves the type of field based on template fields only
   * to know how to send the value to API
   *
   * @param option MatSelectChange object
   */
  changeSearchTemplateField(event: MatSelectChange): void {
    this.searchObject.templateFieldType = this.getTemplateFieldType(event.value);
  }

  /**
   * Clears the search and sends the cleared search parameter to the table-search component.
   */
  clearSearchValue() {
    this.initializeSearch();
    this.sendTableSearch(this.lastSearchField, '');
  }

  /**
   * Triggered when the minus button is clicked. This advanced search option/row is
   * removed from the table-component or global-search by clearing its values and telling the
   * parent to remove it from the view.
   */
  removeSearch() {
    this.clearSearchValue();
    this.clearGlobalSearch();
    this.searchRemoved.next(this.advancedSearchIndex);
  }

  /**
   * Clears all current global search parameters.
   */
  clearGlobalSearch() {
    this.initializeSearch();
  }

  /**
   * Helper function used by the parent SearchComponent the set the search values for a global search
   *
   * @param newSearch The search values to set
   */
  async setGlobalSearch(newSearch: SearchCriteria | SearchCriteria[]): Promise<void> {
    // Must wait for the templates to be loaded
    // Otherwise some fields will be empty
    await this.templatesLoadedPromise;
    if (Array.isArray(newSearch)) {
      this.searchObject = this.createSearchObjectFromArray(newSearch);
    } else {
      this.searchObject = this.createSearchObject(newSearch);
    }

    this.changeDetector.detectChanges();
  }

  /**
   * Returns filtered options for search dropdown according to the field to search,
   * if object doesn't exist on filtered options returns all the search options object
   */
  getSearchOptions(): { [key: string]: string } {
    // When the selected field is a template field the search criteria must be filtered by type
    if (this.searchObject.field === 'template_data') {
      return this.optionsForTemplateFields[this.getTemplateFieldType(this.searchObject.templateField)];
    }

    return this.optionsByField[this.searchObject.field] || this.searchOptions;
  }

  /**
   * When the templates dropdown or from date picker change trigger this event to enable search button
   */
  activateSearch(): void {
    this.templateChanged.emit();
  }

  /**
   * Creates an object with the format SearchFormValues from a saved search object
   *
   * @param search SearchCriteria object from saved search
   */
  private createSearchObject(search: SearchCriteria): SearchFormValues {
    const searchForm: SearchFormValues = {
      subfield: '',
      templateField: '',
      field: search.field,
      value: search.value,
      templateFieldType: '',
      operator: search.operator,
      or: search.or ? 'true' : 'false',
      range: {
        from: null,
        to: null
      }
    };

    // If the search field does not exist in the array it's because is a template data field
    if (
      search.field !== 'search_text' &&
      this.searchFields &&
      Object.keys(this.searchFields).indexOf(search.field) === -1
    ) {
      // When there is a subfield they are separated by dots
      const fields = searchForm.field.split('.');
      searchForm.field = 'template_data';
      searchForm.templateField = fields[0];
      searchForm.subfield = fields[1] || '';
      searchForm.templateFieldType = this.getTemplateFieldType(searchForm.templateField);

      // When the type is checkbox the value will be the subfield since it's a subfield in the query
      if (searchForm.templateFieldType === 'checkbox') {
        searchForm.value = searchForm.subfield;
        searchForm.subfield = '';
      } else if (searchForm.templateFieldType === 'date') {
        // When user saves a range search the value is an array
        searchForm.range.from = Array.isArray(search.value) ? search.value[0] : search.value;
      }
    }

    if (search.operator === SearchCriteriaOperator.Range) {
      searchForm.range.from = search.value[0];
      searchForm.range.to = search.value[1];
    }

    return searchForm;
  }

  /**
   * So far the only scenario when a saved search contains an array of SearchCriteria
   * is when the user saved a search for the "template_data" and the template field is a range date type
   * This function takes the 2 objects and creates a searchFromValues object
   *
   * @param search SearchCriteria array of objects from saved search
   */
  private createSearchObjectFromArray(search: SearchCriteria[]): SearchFormValues {
    const searchForm: SearchFormValues = {
      value: '',
      or: 'false',
      subfield: '',
      templateField: '',
      field: 'template_data',
      templateFieldType: 'range',
      operator: SearchCriteriaOperator.Range,
      range: {
        from: null,
        to: null
      }
    };

    // Both objects in array will have same field with different subfield
    const fields = search[0].field.split('.');
    searchForm.templateField = fields[0];

    // Arrays will come always in the same order the first one is the "from" value and the second one the "to"
    searchForm.range.from = search[0].value;
    searchForm.range.to = search[1].value;

    return searchForm;
  }

  /**
   * Sends the TableSearch to the table-search component.
   *
   * @param field TableSearch's field property
   * @param value TableSearch's value property
   */
  private sendTableSearch(field: string, value: string) {
    if (this.isGlobalSearch) return;
    this.newAdvancedSearch.next({ field: field, value: value });
    this.lastSearchField = field;
  }

  /**
   * Sends all current values to the parent SearchComponent
   */
  private sendGlobalSearch(): void {
    const newSearch: SearchCriteria | SearchCriteria[] = this.createSearchCriteria(this.searchObject);
    // Validate all fields are ok
    this.hasError = !this.isValidSearch(newSearch);

    if (this.hasError) return;

    this.newAdvancedSearch.emit(newSearch);
  }

  /**
   * Creates a search criteria object based in the search object passed
   *
   * @param searchObject Search object with form values
   */
  private createSearchCriteria(searchObject: SearchFormValues): SearchCriteria | SearchCriteria[] {
    let newSearch: SearchCriteria | SearchCriteria[] = { field: '', value: '' };
    newSearch.field = searchObject.field;
    newSearch.value = searchObject.value;
    newSearch.operator = searchObject.operator;

    // Or can be different than false only when the field is not search_text
    if (newSearch.field !== 'search_text') {
      newSearch.or = searchObject.or === 'true';
    }

    // When the search criteria is by template data the field to look is the one
    // In the dropdown of template fields
    if (searchObject.field === 'template_data') {
      newSearch.field = searchObject.templateField;

      // When the selected field is a template field that needs a subfield must be added
      if (searchObject.templateFieldType === 'address') {
        newSearch.field = `${searchObject.templateField}.${searchObject.subfield}`;
      } else if (searchObject.templateFieldType === 'checkbox') {
        // When the search is by a checkbox field the subfield must be the value
        newSearch.field = `${searchObject.templateField}.${searchObject.value.trim()}`;
        newSearch.value = true;
      } else if (searchObject.templateFieldType === 'currency') {
        // For currency types the subfield is always "amount" and the value must be a number
        newSearch.field = `${searchObject.templateField}.amount`;
        newSearch.value = parseFloat(newSearch.value);
      } else if (searchObject.templateFieldType === 'date') {
        newSearch.value = new Date(searchObject.range.from);
      } else if (searchObject.templateFieldType === 'phone') {
        newSearch.value = this.getPhoneValue(newSearch.value || '');
      } else if (searchObject.templateFieldType === 'range') {
        // When is searching for a range template field does a search inclusive in both sub-fields
        newSearch = [
          {
            or: false,
            value: new Date(searchObject.range.from),
            field: `${searchObject.templateField}.from`,
            operator: SearchCriteriaOperator.GreaterEquals
          },
          {
            or: false,
            value: new Date(searchObject.range.to),
            field: `${searchObject.templateField}.to`,
            operator: SearchCriteriaOperator.LessEquals
          }
        ];
      } else if (searchObject.templateFieldType === 'number') {
        newSearch.value = parseFloat(newSearch.value);
      }
    }

    if (searchObject.field === 'document_tag_ids' && !Array.isArray(newSearch)) {
      const { value } = newSearch;
      const newValue = Array.isArray(value) ? value.join(',') : value;
      newSearch.value = newValue;
    }

    // Modifies value depending on the operator
    if (!Array.isArray(newSearch) && newSearch.operator === SearchCriteriaOperator.Range) {
      newSearch.value = [
        formatDate(new Date(searchObject.range.from), 'yyyy-MM-ddTHH:mm:ssZ', 'en-US'),
        formatDate(new Date(searchObject.range.to), 'yyyy-MM-ddTHH:mm:ssZ', 'en-US')
      ];

      // Dates in template fields are saved with the standard iso string
      if (this.searchObject.field === 'template_data') {
        newSearch.value = [new Date(searchObject.range.from), new Date(searchObject.range.to)];
      }
    } else if (
      !Array.isArray(newSearch) &&
      (newSearch.operator === SearchCriteriaOperator.In || newSearch.operator === SearchCriteriaOperator.BangIn)
    ) {
      newSearch.value = newSearch.value
        .split(',')
        .map((term) => term.trim())
        .filter((term) => term);
    }

    return newSearch;
  }

  /**
   * Initialize values for the search object
   */
  private initializeSearch(): void {
    this.searchObject = {
      value: '',
      or: 'false',
      subfield: '',
      templateField: '',
      field: 'search_text',
      templateFieldType: '',
      operator: SearchCriteriaOperator.Partial,
      range: {
        from: null,
        to: null
      }
    };
  }

  /**
   * Returns filtered options according to the field
   *
   * @param fieldName The name of the field to filter the options
   */
  private getOptionsByField(fieldName: string): { [key: string]: string } {
    let excludedOptions = [];

    if (fieldName === 'control_number' || fieldName === 'version') {
      // does NOT include "Date Range", "In" or "Not In"
      excludedOptions = Object.keys(this.searchOptions).filter(
        (key) => key === 'range' || key === 'in' || key === '!in'
      );
    } else if (fieldName === 'created_at' || fieldName === 'shared_at' || fieldName === 'updated_at') {
      // Only allows "Date Range"
      excludedOptions = Object.keys(this.searchOptions).filter((key) => key !== 'range');
    } else if (fieldName === 'description') {
      // Only allows "Contains"
      excludedOptions = Object.keys(this.searchOptions).filter((key) => key !== 'partial');
    } else if (fieldName === 'source' || fieldName === 'title' || fieldName === 'template_data') {
      // Only allows "Contains" or "Equals"
      excludedOptions = Object.keys(this.searchOptions).filter((key) => key !== 'partial' && key !== '=');
    } else if (fieldName === 'template_id') {
      // Only allows Equals"
      excludedOptions = Object.keys(this.searchOptions).filter((key) => key !== '=');
    } else if (fieldName === 'document_tag_ids') {
      // Only allows "Contains" or "Equals"
      excludedOptions = Object.keys(this.searchOptions).filter((key) => key !== '=');
    }

    // Return an object without the excluded options in the array
    return Object.keys(this.searchOptions).reduce((obj: any, key: string) => {
      if (!excludedOptions.includes(key)) {
        obj[key] = this.searchOptions[key];
      }

      return obj;
    }, {});
  }

  /**
   * Returns filtered options according to the type
   *
   * @param type The type of the field
   */
  private getOptionsByFieldType(type: string): { [key: string]: string } {
    // Option for text like fields
    let includedOptions = ['=', 'partial', '!='];
    // Options for numbers
    const numberOptions = ['=', '>', '<', '!=', '>=', '<='];

    switch (type) {
      case 'time':
      case 'checkbox':
        includedOptions = ['='];
        break;
      case 'date':
        includedOptions = ['range'].concat(numberOptions);
        break;
      case 'number':
      case 'currency':
        includedOptions = numberOptions;
        break;
      case 'range':
        includedOptions = ['range'];
        break;
    }

    // Return an object with the options for this type of field
    return Object.keys(this.searchOptions).reduce((obj: any, key: string) => {
      if (includedOptions.includes(key)) {
        obj[key] = this.searchOptions[key];
      }

      return obj;
    }, {});
  }

  /**
   * Returns true if all the required properties for a search exist
   *
   * @param search Search object to evaluate
   */
  private isValidSearch(search: SearchCriteria | SearchCriteria[]): boolean {
    // validates only the first object in case of array since the second one is always
    // created automatically
    search = Array.isArray(search) ? search[0] : search;
    if (!search.field || !search.operator || !search.value) return false;
    return true;
  }

  /**
   * The objects from emptyTypes are objects with keys but all values are empty,
   * in order to use the object values for search fields this function sets the values to the object
   *
   * @param object Object with the fields for search
   */
  private getSearchFields(object: any): { [key: string]: string } {
    return Object.keys(object).reduce((obj: any, value: string) => {
      let transformedValue = this.lowercasePipe.transform(value);
      transformedValue = this.snakeCasePipe.transform(transformedValue);
      transformedValue = this.snakeCasePipe.transform(transformedValue);
      transformedValue = this.titlePipe.transform(transformedValue);
      obj[value] = transformedValue;

      return obj;
    }, {});
  }

  /**
   * Load user templates to show in the dropdown when the search criteria is template name
   */
  private loadUserTemplates(templates: Template[] = [], page: number = 1): Promise<void> {
    return new Promise((resolve, reject) => {
      this.api.templates
        .get({ per_page: 10, page: page })
        .toPromise()
        .then((response) => {
          const totalTemplates = get(response, 'total', -1);
          templates = templates.concat(get(response, 'data', []));

          // If there is more than one page returns a promise to
          // get the next of templates
          if (templates.length < totalTemplates) {
            return resolve(this.loadUserTemplates(templates, ++page));
          }

          // Set all templates to variables for dropdown
          this.templateFields = this.getTemplateFields(templates);
          this.userTemplates = this.getTemplatesForDropdown(templates);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Returns an array with the necessary data for the templates dropdown
   */
  private getTemplatesForDropdown(templates: Template[]): any {
    return templates.map((template) => ({
      id: template.id,
      name: template.name
    }));
  }

  /**
   * Create an array of unique fields with all the templates fields
   */
  private getTemplateFields(templates: Template[]): Array<string> {
    // Creates single array with all fields from all templates
    const fields = templates
      .map((template) => template.fields || [])
      .reduce((fieldsList, fieldsArray) => fieldsList.concat(fieldsArray), []);

    // Remove duplicated fields by label
    return uniqBy(fields, 'label');
  }

  /**
   * Returns the type of subfield based on template fields list label
   *
   * @param templateField template field selected
   */
  private getTemplateFieldType(templateField: string): string {
    let type = '';
    if (!templateField) return type;

    const fieldFromList = this.templateFields.find((field) => field.label === templateField);

    if (fieldFromList) type = fieldFromList.type;

    // date and date range have the same name "date" but different values
    // When the field is a date range the type is changed to range for better handling
    if (type === 'date' && fieldFromList.values.includes('date_range')) type = 'range';

    return type;
  }

  /**
   * Removes underscores from uncompleted masked phone number
   *
   * @param phone masked phone from input field
   */
  private getPhoneValue(phone: string): string {
    // gets the position of the first underscore in the string
    const index = phone.indexOf('_');

    if (index > -1) phone = phone.slice(0, index);

    return phone.trim();
  }
}
